import { io, Socket } from 'socket.io-client';
import { ReviewEdit } from './review';
import { getDocumentBlob } from './wordDraft';
import { getStoredUser } from './auth';
import { SIGNUS_API_URL } from './constants';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';
/* global console */

// socket.io connection requires two separate values:
//   path — the Engine.IO handshake endpoint, must match the server's adapter path
//   url  — server origin + namespace (namespace = path suffix after stripping `path`)
//
// SIGNUS_API_URL = "http://localhost:3001/api/signus"
// → url  = "http://localhost:3001/api/signus/wss/word-full-review-session-results"
// → path = "/api/signus/wss"
//
// socket.io derives namespace as "/word-full-review-session-results" from those two.
const WSS_PATH = '/api/signus/wss';
const WSS_URL = `${SIGNUS_API_URL}/wss/word-full-review-session-results`;
const WSS_EVENT = 'signus-word-full-review-session-updates-wss-event';

type WssPayloadType = 'FullReviewChunkDto' | 'FullReviewCompleteDto' | 'FullReviewErrorDto';

interface WssMessage<T> {
  payload: T;
  payloadType: WssPayloadType;
  serverTime: string;
}

interface ChunkPayload {
  title: string;
  selectedText: string;
  proposedText?: string | null;
  rationale: string;
  operation: 'REPLACE' | 'INSERT_BEFORE' | 'INSERT_AFTER';
}

interface ErrorPayload {
  errorType: string;
}

export interface FullReviewOptions {
  teamSlug: string;
  /** JWT access token — sent as `token` in the socket.io auth handshake. */
  accessToken: string;
  /** Called for each chunk as it arrives — apply it immediately as a redline. */
  onChunk: (edit: ReviewEdit) => void;
  signal?: AbortSignal;
}

export interface FullReviewSessionResult {
  sessionId: string;
}

function chunkToEdit(chunk: ChunkPayload): ReviewEdit | null {
  const { selectedText, proposedText, rationale, operation } = chunk;
  if (!selectedText) return null;

  console.log(
    `[fullReview] chunk operation=${operation} selectedText="${selectedText.slice(0, 60)}" proposedText="${(proposedText ?? '').slice(0, 60)}"`,
  );

  switch (operation) {
    case 'REPLACE':
      return {
        type: 'replace',
        find: selectedText,
        replace_with: proposedText ?? undefined,
        rationale,
      };
    case 'INSERT_AFTER':
      return {
        type: 'insert_after',
        find: selectedText,
        text: proposedText ?? undefined,
        rationale,
      };
    case 'INSERT_BEFORE':
      return {
        type: 'insert_before',
        find: selectedText,
        text: proposedText ?? undefined,
        rationale,
      };
    default:
      return null;
  }
}

async function createSession(blob: Blob, teamSlug: string, accessToken: string): Promise<string> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/full-review/sessions`,
    accessToken,
    token => {
      const form = new FormData();
      form.append('file', blob, 'document.docx');
      return { method: 'POST', headers: { Authorization: token }, body: form };
    },
  );

  const json = await res.json();

  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Full review session failed (${res.status})`),
      extractErrorType(json),
    );
  }

  const payload = (json.payload ?? json) as Record<string, unknown>;
  return payload.sessionId as string;
}

/**
 * Start a full review session and stream chunks back via onChunk.
 * Returns when the session is complete or throws on error/abort.
 */
export async function startFullReview(options: FullReviewOptions): Promise<void> {
  const { teamSlug, accessToken, onChunk, signal } = options;

  const blob = await getDocumentBlob();

  if (signal?.aborted) return;

  const sessionId = await createSession(blob, teamSlug, accessToken);

  console.log(`[fullReview] session created: ${sessionId}`);

  if (signal?.aborted) return;

  await new Promise<void>((resolve, reject) => {
    const socket: Socket = io(WSS_URL, {
      transports: ['websocket'],
      reconnection: false,
      path: WSS_PATH,
      auth: { token: getStoredUser()?.accessToken ?? accessToken, teamSlug, sessionId },
    });

    const cleanup = (): void => {
      socket.disconnect();
    };

    signal?.addEventListener('abort', () => {
      cleanup();
      resolve();
    });

    socket.on('connect', () => {
      console.log(`[fullReview] WSS connected, sessionId: ${sessionId}`);
    });

    socket.on('connect_error', (err: Error) => {
      cleanup();
      reject(new Error(`WSS connection failed: ${err.message}`));
    });

    socket.on(WSS_EVENT, (msg: WssMessage<ChunkPayload | ErrorPayload | null>) => {
      if (signal?.aborted) {
        cleanup();
        resolve();
        return;
      }

      if (msg.payloadType === 'FullReviewChunkDto') {
        const edit = chunkToEdit(msg.payload as ChunkPayload);
        if (edit) onChunk(edit);
      } else if (msg.payloadType === 'FullReviewCompleteDto') {
        console.log(`[fullReview] complete`);
        cleanup();
        resolve();
      } else if (msg.payloadType === 'FullReviewErrorDto') {
        const { errorType } = msg.payload as ErrorPayload;
        console.log(`[fullReview] error from server: ${errorType}`);
        cleanup();
        reject(new Error(`Full review failed: ${errorType}`));
      }
    });

    socket.on('disconnect', (reason: string) => {
      console.log(`[fullReview] disconnected: ${reason}`);
      // Only reject on unexpected disconnects (not our own cleanup)
      if (reason === 'io server disconnect' || reason === 'transport error') {
        reject(new Error(`WSS disconnected unexpectedly: ${reason}`));
      }
    });
  });
}
